import { HeroUIProvider } from "@heroui/react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import * as React from "react";
import { type LoaderFunctionArgs, data as json } from "react-router";
import { type MetaFunction, Outlet, useLoaderData, useNavigate, useRouteError } from "react-router";
import { PreventFlashOnWrongTheme, ThemeProvider, useTheme } from "remix-themes";
import type { ExternalScriptsHandle } from "remix-utils/external-scripts";
import { getAuth } from "~/auth/.server";
import { ToastProvider } from "~/core/toast";
import { getToast } from "~/core/toast/toast.server";
import "~/entry.tailwind.css";
import logrocket from "~/integration/logrocket";
import { useMixpanelIdentify } from "~/integration/mixpanel/client";
import { AuthProvider, useAuth } from "./modules/auth/use-auth";
import { TenantProvider } from "./modules/tenant/use-tenant";
import RootErrorBoundary from "./root.error-boundary";
import RootLayout from "./root.layout";

export type RootLoaderData = SerializeFrom<typeof loader>;

export async function loader(args: LoaderFunctionArgs) {
  const toast = await getToast(args);
  const user = await getAuth({ context: args.context, request: args.request });

  return json(
    {
      prefill: args.context.prefill,
      theme: args.context.theme,
      lang: args.context.lang,
      tenant: args.context.tenant,
      toast: toast.data,
      user,
      configs: args.context.configs,
    },
    { headers: toast.headers },
  );
}

export const links = (() => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    type: "text/css",
    href: "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&family=Outfit:wght@100;200;300;400;500;600;700;800;900&display=swap",
  },
  {
    rel: "stylesheet",
    type: "text/css",
    href: "/style",
  },
  {
    rel: "stylesheet",
    href: "https://cdnjs.cloudflare.com/ajax/libs/mapbox-gl/3.5.2/mapbox-gl.min.css",
    type: "text/css",
  },
]) satisfies LinksFunction;

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { tagName: "link", rel: "shortcut icon", href: data?.tenant.icon },
    { title: data?.tenant.title },
  ];
};

export const handle: ExternalScriptsHandle = {
  scripts: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/mapbox-gl/3.5.2/mapbox-gl.min.js",
      preload: true,
      async: true,
    },
  ],
};

export const Layout = RootLayout;

function _ErrorBoundary() {
  const error = useRouteError();

  captureRemixErrorBoundaryError(error);

  return <RootErrorBoundary />;
}

export const ErrorBoundary = process.env.NODE_ENV === "development" ? undefined : _ErrorBoundary;

function Analytics({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  useMixpanelIdentify(user);

  React.useEffect(() => {
    if (!user || !user.email) {
      return;
    }

    logrocket.identify(user.userId, user.email, `${user.firstName} ${user.lastName}`);
  }, [user]);

  return <>{children}</>;
}

function App() {
  const data = useLoaderData<typeof loader>();

  return (
    <AppThemeProvider>
      <AppHeroUIProvider>
        <AuthProvider value={data.user}>
          <TenantProvider value={data.tenant}>
            <Analytics>
              <Outlet />
            </Analytics>
          </TenantProvider>
        </AuthProvider>
        <ToastProvider />
      </AppHeroUIProvider>
    </AppThemeProvider>
  );
}

export default withSentry(App);

function AppHeroUIProvider({ children }: { children: React.ReactNode }) {
  const data = useLoaderData<typeof loader>();
  const navigate = useNavigate();
  const [theme] = useTheme();

  const locale = React.useMemo(() => {
    if (typeof data.lang === "string") {
      if (data.lang === "en") {
        return "en-US";
      }

      return "fr-CA";
    }

    return undefined;
  }, [data.lang]);

  return (
    <>
      <HeroUIProvider
        locale={locale}
        navigate={navigate}
        className="flex h-full flex-1 flex-col"
        data-theme={theme}
        validationBehavior="aria" // TODO: Try native
      >
        {children}
      </HeroUIProvider>
      <PreventFlashOnWrongTheme ssrTheme={Boolean(data.theme)} />
    </>
  );
}

function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme } = useLoaderData<typeof loader>();

  return (
    <ThemeProvider
      specifiedTheme={theme}
      themeAction="/theme"
    >
      {children}
    </ThemeProvider>
  );
}
